import { useEffect, useState } from 'react';

import { Composer } from '@/components/chat/Composer';
import { MessageList } from '@/components/chat/MessageList';
import { Button } from '@/components/ui/button';
import { resolves } from '@/lib/selectors';
import { useApp } from '@/store/useApp';

export function ChatPane() {
  const messages = useApp((s) => s.messages);
  const isStreaming = useApp((s) => s.isStreaming);
  const sendMessage = useApp((s) => s.sendMessage);
  const stop = useApp((s) => s.stop);
  const stopAndDiscard = useApp((s) => s.stopAndDiscard);
  // Select the underlying array (stable reference until comments actually change),
  // then derive the draft + stale counts in render. Selecting `s.comments.filter(...)`
  // inline returns a new array every store update and creates an infinite re-render
  // loop — every unrelated state change triggers a re-render which triggers another.
  const comments = useApp((s) => s.comments);
  const artifactHtml = useApp((s) => s.artifactHtml);
  const drafts = comments.filter((c) => c.status === 'draft');
  const queuedComments = drafts.length;
  // A draft comment is "stale" if its selector no longer matches anything in the
  // currently rendered design (the model removed/restructured the target). Sending
  // stale comments produces "selector did not match" skips on the next edit, so we
  // surface the count up-front instead of after-the-fact.
  const staleComments = artifactHtml
    ? drafts.filter((c) => !resolves(artifactHtml, c.selector)).length
    : 0;

  return (
    <section
      aria-label="Chat"
      className="flex h-full flex-col bg-white dark:bg-neutral-950"
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium uppercase tracking-widest text-neutral-500">Chat</p>
          {queuedComments > 0 && !isStreaming && (
            <>
              <span
                className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                title="Targeted comments queued in the composer; press Send to dispatch them all"
              >
                {queuedComments} comment{queuedComments === 1 ? '' : 's'} queued
              </span>
              {staleComments > 0 && (
                <span
                  className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800 dark:bg-red-900/40 dark:text-red-200"
                  title="Some queued comments target elements that no longer exist in the current design — they'll be skipped when you Send. Re-click the elements in Comment mode to capture fresh selectors."
                >
                  {staleComments} stale
                </span>
              )}
            </>
          )}
        </div>
        {isStreaming && <StreamingStatus onStop={stop} />}
      </header>
      <div className="min-h-0 flex-1 overflow-hidden">
        <MessageList messages={messages} />
      </div>
      <Composer
        isStreaming={isStreaming}
        onSend={(text) => {
          void sendMessage(text);
        }}
        onStop={stop}
        onStopAndDiscard={() => {
          void stopAndDiscard();
        }}
      />
    </section>
  );
}

function StreamingStatus({ onStop }: { onStop: () => void }) {
  const progress = useApp((s) => s.streamProgress);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!progress) {
    return (
      <Button size="sm" variant="destructive" onClick={onStop}>
        Stop
      </Button>
    );
  }
  const elapsed = Math.max(0, Math.round((now - progress.startedAt) / 1000));
  const idle = Math.max(0, Math.round((now - progress.lastBytesAt) / 1000));
  const bytes = progress.totalBytes;
  const idleWarning = bytes > 0 && idle >= 15;
  const shortModel = progress.model.replace(/^claude-/, '').replace(/-\d{8}$/, '');
  return (
    <div className="flex items-center gap-2">
      <span
        className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[10px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"
        title={`Model in use this turn: ${progress.model}`}
      >
        {shortModel}
      </span>
      <span
        className="font-mono text-[11px] text-neutral-500"
        title={`${bytes.toLocaleString()} bytes received`}
      >
        {bytes === 0 ? `connecting · ${elapsed}s` : `${formatBytes(bytes)} · ${elapsed}s`}
      </span>
      {idleWarning && (
        <span
          className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
          title="No new data from the model recently — the prompt may be too large or the model is taking time to think"
        >
          idle {idle}s
        </span>
      )}
      <Button size="sm" variant="destructive" onClick={onStop}>
        Stop
      </Button>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
